import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Coffee, Wifi, Lock, KeyRound, Clock, BookOpen, UtensilsCrossed, CreditCard, Loader2, MapPin } from "lucide-react";
import { api, apiErr, mediaUrl } from "../lib/api";

const T = {
  fr: { hello: "Bonjour", ready: "Tout est prêt pour votre arrivée à", stay: "Séjour", toPay: "Pour accéder à vos informations d'arrivée, merci de régler :", balance: "Solde du séjour", tax: "Taxe de séjour", total: "Total", pay: "Payer par carte", secure: "Paiement sécurisé par Stripe", room: "Code de votre chambre", door: "Code porte d'entrée", breakfast: "Petit-déjeuner", week: "Du lundi au vendredi", weekend: "Samedi et dimanche", wifi: "Wi-Fi", pass: "Mot de passe", checkout: "Départ au plus tard", livret: "Livret d'accueil", meal: "Commander un repas", thanks: "Merci, votre règlement est bien reçu !", checking: "Vérification du paiement…", expired: "Ce lien a expiré.", invalid: "Lien invalide.", steps: "Votre arrivée pas à pas", step: "Étape" },
  en: { hello: "Hello", ready: "Everything is ready for your arrival at", stay: "Stay", toPay: "To access your arrival information, please pay:", balance: "Stay balance", tax: "Tourist tax", total: "Total", pay: "Pay by card", secure: "Secure payment by Stripe", room: "Your room code", door: "Front door code", breakfast: "Breakfast", week: "Monday to Friday", weekend: "Saturday and Sunday", wifi: "Wi-Fi", pass: "Password", checkout: "Check-out by", livret: "Welcome booklet", meal: "Order a meal", thanks: "Thank you, your payment has been received!", checking: "Checking payment…", expired: "This link has expired.", invalid: "Invalid link.", steps: "Your arrival step by step", step: "Step" },
};

const fmtDate = (d, lang) => (d ? new Date(d + "T12:00:00").toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long" }) : "");

function InfoCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-3xl bg-white/80 ring-1 ring-brand/10 p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3 mb-2">
        <span className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Icon size={20} /></span>
        <h2 className="font-heading text-lg text-brand">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function AccessInfo({ a, t }) {
  return (
    <div className="space-y-4" data-testid="arrival-access">
      {a.room_code && (
        <div className="rounded-3xl bg-brand text-white p-6 text-center shadow-md" data-testid="arrival-room-code">
          <div className="flex items-center justify-center gap-2 mb-1"><KeyRound size={20} /><span className="font-heading text-lg">{t.room}</span></div>
          <p className="font-heading text-3xl sm:text-4xl tracking-wide my-2 break-words">{a.room_code}</p>
        </div>
      )}
      {(a.arrival_steps || []).length > 0 && (
        <InfoCard icon={MapPin} title={t.steps} testid="arrival-steps">
          <ol className="space-y-4 mt-2">
            {a.arrival_steps.map((st, i) => (
              <li key={i} className="space-y-2" data-testid={`arrival-step-view-${i}`}>
                {st.image && <img src={mediaUrl(st.image)} alt={`${t.step} ${i + 1}`} className="w-full rounded-2xl object-cover max-h-80" loading="lazy" />}
                {st.text && <p className="text-sm text-ink-700 whitespace-pre-line"><b className="text-brand">{i + 1}.</b> {st.text}</p>}
              </li>
            ))}
          </ol>
        </InfoCard>
      )}
      {a.door_code && (
        <InfoCard icon={Lock} title={t.door} testid="arrival-door-code"><p className="font-heading text-3xl tracking-widest text-ink-900">{a.door_code}</p></InfoCard>
      )}
      <InfoCard icon={Coffee} title={t.breakfast} testid="arrival-breakfast">
        {a.breakfast_note && <p className="text-sm font-medium text-brand mb-1">{a.breakfast_note}</p>}
        <p className="text-sm text-ink-700">{t.week} : <b>{a.breakfast_week || "8h00 – 10h00"}</b></p>
        <p className="text-sm text-ink-700">{t.weekend} : <b>{a.breakfast_weekend || "8h30 – 10h30"}</b></p>
        {a.breakfast_details && <p className="text-sm text-ink-700 whitespace-pre-line mt-2" data-testid="arrival-breakfast-details">{a.breakfast_details}</p>}
      </InfoCard>
      <InfoCard icon={Wifi} title={t.wifi} testid="arrival-wifi">
        <p className="text-sm text-ink-700">SSID : <b>{a.wifi_ssid}</b></p>
        <p className="text-sm text-ink-700">{t.pass} : <b>{a.wifi_password}</b></p>
      </InfoCard>
      <InfoCard icon={Clock} title={t.checkout} testid="arrival-checkout"><p className="text-sm text-ink-700"><b>{a.checkout || "11h00"}</b></p></InfoCard>
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/livret" className="rounded-full bg-white ring-1 ring-brand/30 text-brand py-3 text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-50 transition-colors" data-testid="arrival-livret-link"><BookOpen size={16} /> {t.livret}</Link>
        <Link to="/commander-repas" className="rounded-full bg-white ring-1 ring-brand/30 text-brand py-3 text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-50 transition-colors" data-testid="arrival-meal-link"><UtensilsCrossed size={16} /> {t.meal}</Link>
      </div>
    </div>
  );
}

export default function ArrivalPage() {
  const { token } = useParams();
  const [lang, setLang] = useState("fr");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState("");
  const t = T[lang];
  const justPaid = new URLSearchParams(window.location.search).get("paid") === "1";

  useEffect(() => {
    let tries = 0;
    let timer;
    const load = () => api.get(`/arrivee/${token}`).then((r) => {
      setData(r.data);
      if (justPaid && !r.data.paid && tries++ < 8) timer = setTimeout(load, 2500);
    }).catch((e) => setError(e?.response?.status === 410 ? "expired" : "invalid"));
    load();
    return () => clearTimeout(timer);
  }, [token, justPaid]);

  const pay = async () => {
    setPaying(true); setPayErr("");
    try { const r = await api.post(`/arrivee/${token}/checkout`, { origin_url: window.location.origin }); window.location.assign(r.data.url); }
    catch (e) { setPayErr(apiErr(e)); setPaying(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#eaf5fb 0%,#f7fbfd 100%)" }} data-testid="arrival-page">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>Votre arrivée · La Bégaudière</title></Helmet>
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-12 pb-12">
        <div className="flex justify-end gap-1 mb-2">
          {["fr", "en"].map((l) => (
            <button key={l} onClick={() => setLang(l)} className={`rounded-full px-3 py-1 text-xs font-medium ${lang === l ? "bg-brand text-white" : "bg-white text-ink-600 ring-1 ring-brand/20"}`} data-testid={`arrival-lang-${l}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <img src="/logo-begaudiere.webp" alt="La Bégaudière" className="h-24 mx-auto mb-4 object-contain" />
        {error && <p className="text-center text-ink-600 mt-8" data-testid="arrival-error">{t[error]}</p>}
        {!error && !data && <Loader2 className="mx-auto animate-spin text-brand" />}
        {data && (
          <>
            <div className="text-center mb-6">
              <h1 className="font-heading text-4xl text-brand" data-testid="arrival-hello">{t.hello} {data.prenom || data.nom} !</h1>
              <p className="text-ink-600 text-sm mt-2">{t.ready} <b>{data.accommodation_name}</b></p>
              <p className="text-ink-400 text-xs mt-1">{t.stay} : {fmtDate(data.arrivee, lang)} → {fmtDate(data.depart, lang)}</p>
            </div>
            {data.paid ? (
              <>
                {justPaid && <p className="text-center text-emerald-700 text-sm font-medium mb-4" data-testid="arrival-thanks">{t.thanks}</p>}
                <AccessInfo a={data.access || {}} t={t} />
              </>
            ) : justPaid ? (
              <p className="text-center text-ink-600 text-sm" data-testid="arrival-checking">{t.checking}</p>
            ) : (
              <div className="rounded-3xl bg-white ring-1 ring-brand/10 p-6 shadow-sm" data-testid="arrival-pay-card">
                <p className="text-sm text-ink-700 mb-4">{t.toPay}</p>
                {data.balance > 0 && <div className="flex justify-between text-sm py-1"><span>{t.balance}</span><b>{data.balance.toFixed(2)} €</b></div>}
                {data.tourist_tax > 0 && <div className="flex justify-between text-sm py-1"><span>{t.tax}</span><b>{data.tourist_tax.toFixed(2)} €</b></div>}
                <div className="flex justify-between text-base py-2 mt-1 border-t border-black/5"><span>{t.total}</span><b data-testid="arrival-due">{data.due.toFixed(2)} €</b></div>
                <button onClick={pay} disabled={paying} className="mt-4 w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors" data-testid="arrival-pay-btn">
                  {paying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} {t.pay}
                </button>
                {payErr && <p className="text-sm text-red-600 text-center mt-2" data-testid="arrival-pay-error">{payErr}</p>}
                <p className="text-[11px] text-ink-400 text-center mt-2">{t.secure}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
